import {Localized, useLocalization} from "@fluent/react";
import DoneIcon from "@mui/icons-material/Done";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {
	Button,
	Container,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Radio,
	RadioGroup,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import React, {useContext, useMemo, useState} from "react";

import AssessmentDeliveryContext from "../AssessmentDeliveryContext";
import SidebarBody from "../sidebars/SidebarBody";
import SidebarContext from "../sidebars/SidebarContext";
import SidebarHeader from "../sidebars/SidebarHeader";
import SidebarSection from "../sidebars/SidebarSection";
import selectChapter from "../../store/chapters/selectChapter";
import type FetchStatus from "../../store/FetchStatus";
import {useAppSelector} from "../../store/hooks";
import {keyProvider} from "../../store/keyProvider";
import contentIssueService from "../../store/services/contentIssueService";
import useSnackbar from "../../store/ui/useSnackbar";
import AppBarDependentDrawer from "../../utils/AppBarDependentDrawer";
import ProgressButton from "../../utils/ProgressButton";
import TextEditor from "../../utils/TextEditor";

type IssueType = "mistake" | "obscure" | "technical";

function ReportIssueSidebar(props: {
	open: boolean;
	width: number | string;
	sectionKey: string;
	courseId: number;
	mobileMode: boolean;
}) {
	const {chapterExerciseKey, close, fullscreen, toggleFullscreen} = useContext(
		SidebarContext
	);

	const [type, setType] = useState<IssueType | "">("");
	const [description, setDescription] = useState("");

	const [empty, setEmpty] = useState<{type?: boolean; description?: boolean}>(
		{}
	);

	const {makeSelectChapterExercise, makeSelectSection} = useContext(
		AssessmentDeliveryContext
	);

	const selectChapterExercise = useMemo(makeSelectChapterExercise, [
		makeSelectChapterExercise,
	]);

	const exercise = useAppSelector(
		(state) =>
			(chapterExerciseKey &&
				selectChapterExercise(state, chapterExerciseKey)) ||
			null
	);

	const selectSection = useMemo(makeSelectSection, [makeSelectSection]);

	const section = useAppSelector((state) =>
		selectSection(state, props.sectionKey)
	);

	const chapter = useAppSelector(
		(state) =>
			(section?.chapterId &&
				selectChapter(state, keyProvider.chapter(section.chapterId))) ||
			null
	);

	const [sendStatus, setSendStatus] = useState<FetchStatus>("none");

	const {l10n} = useLocalization();
	const showSnackbar = useSnackbar();

	async function send() {
		if (!chapter?.id || !section?.id) {
			return;
		}

		if (!validate()) {
			return;
		}

		setSendStatus("pending");

		try {
			await contentIssueService.reportContentIssue(props.courseId, {
				chapterId: chapter.id,
				exerciseId: exercise?.id || section.id,
				type: type,
				description: description,
			});

			setSendStatus("succeeded");
		} catch {
			showSnackbar("error", l10n.getString("error-general"));
			setSendStatus("failed");
		}
	}

	function validate() {
		const emptyFields: typeof empty = {
			type: type === "",
			description: description === "",
		};

		setEmpty(emptyFields);

		return !emptyFields.description && !emptyFields.type;
	}

	function clearAndClose() {
		setDescription("");
		setType("");
		setSendStatus("none");
		setEmpty({});

		close();
	}

	const header = (
		<SidebarHeader
			title={
				<Localized id="report-issue-sidebar-title">Report an issue</Localized>
			}
			fullscreen={fullscreen}
			closureDisabled={sendStatus === "pending"}
			onToggleFullscreen={props.mobileMode ? undefined : toggleFullscreen}
			onClose={clearAndClose}
		/>
	);

	const drawerProps: Omit<
		Parameters<typeof AppBarDependentDrawer>[0],
		"children"
	> = {
		open: props.open,
		variant: props.mobileMode || fullscreen ? "temporary" : "persistent",
		width: props.width,
		anchor: "right",
		topFixedPanel: header,
		unmountOnExit: true,
		onClose: close,
	};

	if (sendStatus === "succeeded") {
		return (
			<AppBarDependentDrawer {...drawerProps}>
				<Success onClose={clearAndClose} />
			</AppBarDependentDrawer>
		);
	}

	let itemTitle;
	if (chapterExerciseKey) {
		itemTitle = `${chapter?.number}.${section?.number}.${exercise?.number} ${exercise?.title}`;
	} else {
		itemTitle = `${chapter?.number}.${section?.number} ${section?.title}`;
	}

	return (
		<AppBarDependentDrawer
			{...drawerProps}
			bottomFixedPanel={
				<Stack
					direction="row"
					spacing={2}
					sx={{p: 2, justifyContent: "flex-end"}}
				>
					<Button disabled={sendStatus === "pending"} onClick={clearAndClose}>
						<Localized id="report-issue-sidebar-action-cancel">
							Cancel
						</Localized>
					</Button>
					<ProgressButton
						variant="contained"
						inProgress={sendStatus === "pending"}
						onClick={send}
					>
						<Localized id="report-issue-sidebar-action-send">Send</Localized>
					</ProgressButton>
				</Stack>
			}
		>
			<SidebarBody>
				<SidebarSection
					title={
						<Localized id="report-issue-sidebar-field-found-in-label">
							Content issue found in
						</Localized>
					}
				>
					<Typography>{itemTitle}</Typography>
				</SidebarSection>

				<SidebarSection
					title={
						<Typography
							variant="inherit"
							color={empty.type ? "error" : "inherit"}
						>
							<Localized id="report-issue-sidebar-field-issue-type-label">
								Content issue type
							</Localized>
						</Typography>
					}
				>
					<FormControl>
						<RadioGroup
							aria-label={l10n.getString(
								"report-issue-sidebar-field-issue-type-label"
							)}
							value={type}
							onChange={(_, v) => {
								setType(v as typeof type);
								setEmpty((prev) => ({...prev, type: false}));
							}}
						>
							<FormControlLabel
								value="mistake"
								control={<Radio />}
								label={
									<IssueLabel type="mistake">
										<Localized id="report-issue-sidebar-field-label-issue-type-option-mistake">
											Mistake in content
										</Localized>
									</IssueLabel>
								}
							/>
							<FormControlLabel
								value="obscure"
								control={<Radio />}
								label={
									<IssueLabel type="obscure">
										<Localized id="report-issue-sidebar-field-issue-type-option-obscure">
											Difficult to understand
										</Localized>
									</IssueLabel>
								}
							/>
							<FormControlLabel
								value="technical"
								control={<Radio />}
								label={
									<IssueLabel type="technical">
										<Localized id="report-issue-sidebar-field-label-issue-type-option-technical">
											Technical issue in content
										</Localized>
									</IssueLabel>
								}
							/>
						</RadioGroup>
					</FormControl>

					{empty.type && (
						<FormHelperText error>
							<Localized id="report-issue-sidebar-error-required-field">
								This field is required
							</Localized>
						</FormHelperText>
					)}

					<FormHelperText>
						<Localized id="report-issue-sidebar-notice-on-platform-issues">
							Note! This form is only for providing content feedback. For any
							urgent issues, please contact your teacher or institution
							directly.
						</Localized>
					</FormHelperText>
				</SidebarSection>

				<SidebarSection
					title={
						<Typography
							variant="inherit"
							color={empty.description ? "error" : "inherit"}
						>
							<Localized id="report-issue-sidebar-field-issue-descr-label">
								Content issue description
							</Localized>
						</Typography>
					}
				>
					<TextEditor
						key={type}
						mode="basic"
						value={description}
						readonly={sendStatus === "pending"}
						placeholder={l10n.getString(
							"report-issue-sidebar-descr-placeholder",
							{type: type}
						)}
						onChange={(v) => {
							setDescription(v);
							setEmpty((prev) => ({...prev, description: false}));
						}}
					/>

					{empty.description && (
						<FormHelperText error>
							<Localized id="report-issue-sidebar-error-required-field">
								This field is required
							</Localized>
						</FormHelperText>
					)}
				</SidebarSection>
			</SidebarBody>
		</AppBarDependentDrawer>
	);
}

function IssueLabel(props: {type: IssueType; children: React.ReactNode}) {
	return (
		<Stack direction="row" spacing={1} sx={{alignItems: "center"}}>
			{props.children}
			<Tooltip
				title={
					<Localized
						id="report-issue-sidebar-tooltip-issue-type"
						vars={{type: props.type}}
					>
						Report content that you feel is confusing or poorly presented.
					</Localized>
				}
			>
				<InfoIcon color="action" fontSize="inherit" />
			</Tooltip>
		</Stack>
	);
}

function Success(props: {onClose: () => void}) {
	return (
		<Container
			sx={{
				height: 1,
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<DoneIcon sx={{height: "50px", width: "50px"}} color="primary" />
			<Typography variant="h6" align="center">
				<Localized id="report-issue-sidebar-result-success-title">
					Success
				</Localized>
			</Typography>
			<Typography align="center">
				<Localized id="report-issue-sidebar-result-success-descr">
					Your content issue report was successfully sent
				</Localized>
			</Typography>
			<Typography variant="caption" align="center" sx={{mt: 2}}>
				<Localized id="report-issue-sidebar-notice-on-platform-issues">
					Note! This form is only for providing content feedback. For any urgent
					issues, please contact your teacher or institution directly.
				</Localized>
			</Typography>
			<Button onClick={props.onClose} sx={{mt: 2}}>
				<Localized id="report-issue-sidebar-result-success-action-close">
					Close
				</Localized>
			</Button>
		</Container>
	);
}

export default ReportIssueSidebar;
